import React, { memo } from 'react';
import { View, Text } from 'react-native';
import { StyleSheet } from 'react-native-unistyles';
import Input from '@/components/General/inputs/Input';
import SelectInput from '@/components/General/inputs/SelectInput';
import DatePickerInput from '@/components/General/inputs/DatePickerInput';
import ToggleInput from '@/components/General/inputs/ToggleInput';
import SheetSelector from '@/components/General/inputs/SheetSelector';

/**
 * Dynamic filter input component that renders the appropriate input based on field type
 * @param {object} props
 * @param {object} props.condition - The filter condition object
 * @param {Function} props.onValueChange - Callback when value changes
 * @param {Function} props.onValue2Change - Callback when second value changes (for between)
 * @param {Array} props.dynamicOptionsData - Data for dynamic options (e.g., roles)
 */
const FilterInput = ({ condition, onValueChange, onValue2Change, dynamicOptionsData = [] }) => {
    const { fieldType, operator, value, value2, options, dynamicOptions } = condition;
    
    // Don't show input for empty checks
    if (['is_empty', 'is_not_empty'].includes(operator)) {
        return (
            <View style={styles.emptyMessage}>
                <Text style={styles.emptyText}>No value needed</Text>
            </View>
        );
    }
    
    const isBetween = operator === 'between';

    // Render based on field type
    switch (fieldType) {
        case 'text':
            return (
                <View style={styles.inputContainer}>
                    <Input
                        title="Value"
                        placeholder="Enter text"
                        value={value}
                        onUpdate={onValueChange}
                    />
                </View>
            );
            
        case 'number':
            return (
                <View style={styles.inputContainer}>
                    <View style={styles.rangeContainer}>
                        <View style={isBetween ? styles.halfWidth : styles.fullWidth}>
                            <Input
                                title={isBetween ? "From" : "Value"}
                                placeholder={isBetween ? "From Value" : "Enter number"}
                                value={value?.toString() || ''}
                                type="numeric"
                                onUpdate={(val) => onValueChange(val ? Number(val) : '')}
                            />
                        </View>
                        {isBetween && (
                            <View style={styles.halfWidth}>
                                <Input
                                    title="To"
                                    placeholder="To"
                                    value={value2?.toString() || ''}
                                    type="numeric"
                                    onUpdate={(val) => onValue2Change(val ? Number(val) : '')}
                                />
                            </View>
                        )}
                    </View>
                </View>
            );
            
        case 'date':
            // Handle past_n_days operator with a number input
            if (operator === 'past_n_days') {
                return (
                    <View style={styles.inputContainer}>
                        <Input
                            title="Number of Days"
                            placeholder="Enter a number of days"
                            helpText="The number of days preceding the current date will be matched in the filter"
                            value={value?.toString() || ''}
                            type="numeric"
                            onUpdate={(val) => onValueChange(val ? Number(val) : '')}
                        />
                    </View>
                );
            }
            
            return (
                <View style={styles.inputContainer}>
                    <View style={styles.rangeContainer}>
                        <View style={isBetween ? styles.halfWidth : styles.fullWidth}>
                            <DatePickerInput
                                title={isBetween ? "From" : "Date"}
                                mode="date"
                                value={value}
                                onUpdate={onValueChange}
                            />
                        </View>
                        {isBetween && (
                            <View style={styles.halfWidth}>
                                <DatePickerInput
                                    title="To"
                                    mode="date"
                                    value={value2}
                                    onUpdate={onValue2Change}
                                />
                            </View>
                        )}
                    </View>
                </View>
            );
            
        case 'select': {
            // Use options from condition or dynamic options
            const selectOptions = options?.length > 0 
                ? options 
                : dynamicOptionsData?.map(o => typeof o === 'object' ? o.name || o.label : o) || [];
            
            return (
                <View style={styles.inputContainer}>
                    <SelectInput
                        title="Value"
                        placeholder="Select value"
                        value={value}
                        options={selectOptions}
                        onUpdate={onValueChange}
                    />
                </View>
            );
        }
            
        case 'multiselect': {
            // Handle dynamic options (like roles)
            if (dynamicOptions === 'roles') {
                return (
                    <View style={styles.inputContainer}>
                        <SheetSelector
                            title="Select Values"
                            type="roles"
                            multiple
                            value={Array.isArray(value) ? dynamicOptionsData?.filter(r => value.includes(r.id)) : []}
                            onChange={(selected) => onValueChange(selected?.map(s => s.id) || [])}
                        />
                    </View>
                );
            }

            // Handle dynamic options (like small group types)
            if (dynamicOptions === 'smallGroupTypes') {
                return (
                    <View style={styles.inputContainer}>
                        <SheetSelector
                            title="Select Values"
                            type="smallGroupTypes"
                            multiple
                            value={Array.isArray(value) ? dynamicOptionsData?.filter(r => value.includes(r.id)) : []}
                            onChange={(selected) => onValueChange(selected?.map(s => s.id) || [])}
                        />
                    </View>
                );
            }

            // Use static options
            const multiOptions = options?.length > 0 ? options : [];
            
            if (multiOptions.length > 0) {
                return (
                    <View style={styles.inputContainer}>
                        <ToggleInput
                            title="Select Values"
                            items={multiOptions}
                            selectedValues={Array.isArray(value) ? value : []}
                            enableSelectAll={multiOptions.length > 3}
                            itemsPerRow={multiOptions.length > 4 ? 3 : multiOptions.length}
                            onItemChanged={(item, new_value) => {
                                const currentValues = Array.isArray(value) ? value : [];
                                const index = currentValues.indexOf(item)
                                index == -1 ? (new_value && currentValues.push(item)) : (!new_value && currentValues.splice(index, 1))
                                onValueChange(currentValues)
                            }}
                            onSelectAll={(formatted, selectAll) => {
                                onValueChange(selectAll ? formatted.map(item => item.value) : [])
                            }}
                        />
                    </View>
                );
            }
            
            return (
                <View style={styles.inputContainer}>
                    <Input
                        placeholder="Enter values (comma separated)"
                        value={Array.isArray(value) ? value.join(', ') : value}
                        onUpdate={(val) => onValueChange(val.split(',').map(v => v.trim()).filter(Boolean))}
                    />
                </View>
            );
        }
            
        case 'boolean':
            return (
                <View style={styles.inputContainer}>
                    <ToggleInput
                        items={[
                            { label: 'Enabled', value: 'true' },
                            { label: 'Disabled', value: 'false' },
                        ]}
                        selectedValues={[value === 'true' ? 'true' : 'false']}
                        onItemChanged={(item) => onValueChange(item)}
                    />
                </View>
            );
            
        case 'sheetselector': {
            const selectorType = condition.sheetSelectorType || 'people';
            return (
                <View style={styles.inputContainer}>
                    <SheetSelector
                        title="Select Value"
                        type={selectorType}
                        value={value}
                        onChange={(selected) => onValueChange(selected)}
                    />
                </View>
            );
        }
            
        default:
            return (
                <View style={styles.inputContainer}>
                    <Input
                        placeholder="Enter value"
                        value={value?.toString() || ''}
                        onUpdate={onValueChange}
                    />
                </View>
            );
    }
};

const styles = StyleSheet.create(({ colors }) => ({
    inputContainer: {
        flex: 1,
        minWidth: 150,
    },
    rangeContainer: {
        flexDirection: 'row',
        gap: 10,
        alignItems: 'flex-start',
    },
    halfWidth: {
        flex: 1,
    },
    fullWidth: {
        flex: 1,
    },
    emptyMessage: {
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        paddingVertical: 15,
    },
    emptyText: {
        color: colors.textSecondary,
        fontStyle: 'italic',
        fontSize: 13,
    },
}));

export default memo(FilterInput);
